import { Box, Typography, Skeleton } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Overview as OverviewType } from "../../../types/types/user.types";
import {
  AgeIcon,
  WeightIcon,
  HeightIcon,
  StrongFootIcon,
  LocationsIcon,
  CoachedInIcon,
  TotalCareerTransfersIcon,
  TotalRepresentedIcon,
  RepresentedClubsIcon,
  ScoutedPlayersIcon,
  TeamsCoachedIcon,
  FormationsIcon,
} from "../assets/icons/player-icons";

const overViewIconMapper = {
  age: <AgeIcon />,
  weight: <WeightIcon />,
  height: <HeightIcon />,
  strongFoot: <StrongFootIcon />,
  locations: <LocationsIcon />,
  totalScoutedPlayers: <ScoutedPlayersIcon />,
  representedClub: <RepresentedClubsIcon />,
  totalRepresented: <TotalRepresentedIcon />,
  totalCareerTransfers: <TotalCareerTransfersIcon />,
  countriesCoachedIn: <CoachedInIcon />,
  totalTeamsCoached: <TeamsCoachedIcon />,
  preferredFormations: <FormationsIcon />,
};

const Overview = ({
  overViewTitle,
  overViewList,
  isLoading,
}: {
  overViewTitle: string;
  overViewList?: OverviewType[];
  isLoading: boolean;
}) => {
  const { t } = useTranslation("profiles", { keyPrefix: "overview" });

  if (isLoading) return <OverviewSkeleton overViewTitle={overViewTitle} />;

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{t(overViewTitle)}</Typography>
      <Box sx={styles.cardsWrapper}>
        {overViewList?.map((item) => (
          <Box key={item.key} sx={styles.card}>
            {overViewIconMapper[item.key as keyof typeof overViewIconMapper]}
            <Box sx={styles.cardContent}>
              <Typography sx={styles.cardTitle}>{item.title}</Typography>
              <Typography sx={styles.cardValue}>
                {Array.isArray(item.value) ? item.value.join(", ") : item.value ?? "--"}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Overview;

const OverviewSkeleton = ({ overViewTitle }: { overViewTitle: string }) => {
  const { t } = useTranslation("profiles", { keyPrefix: "overview" });

  return (
    <Box sx={styles.container}>
      <Typography sx={styles.title}>{t(overViewTitle)}</Typography>
      <Box sx={styles.cardsWrapper}>
        {[...Array(4)].map((_, index) => (
          <Box key={index} sx={styles.card}>
            <Skeleton variant="circular" width={28} height={28} />
            <Box sx={styles.cardContent}>
              <Skeleton variant="text" width={80} height={20} />
              <Skeleton variant="text" width={60} height={28} />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const styles = {
  container: { display: "flex", flexDirection: "column", gap: "16px" },
  title: {
    color: "var(--black-100, #1C1C1C)",
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "24px",
  },
  cardsWrapper: {
    display: "flex",
    flexDirection: { xs: "column", md: "row" },
    gap: "8px",
    flexWrap: "wrap",
    width: "100%",
  },
  card: {
    display: "flex",
    flexDirection: "row",
    gap: "8px",
    flexWrap: "wrap",
    width: { xs: "100%", md: "calc(50% - 4px)" },
    borderRadius: "12px",
    border: "1px solid var(--Gray-200, #E4E7EC)",
    padding: "16px",
  },
  cardContent: { display: "flex", flexDirection: "column", gap: "4px" },
  cardTitle: {
    color: "var(--Gray-600, #475467)",
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  cardValue: {
    color: "var(--Gray-900, #101828)",
    fontFamily: "Montserrat",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
  },
};
